import { Typography } from '@euler/app/components/typography';
import { buildAudioWaveform } from '@euler/app/flows/chat/utils/buildAudioWaveform';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { aiColors, tint } from '../../../../../ai-suite/theme';
import { formatVoiceDuration } from '../../../functions/formatVoiceDuration';
import { waveformBars } from '../../../functions/traineeVoice';
import { LinePosition, LineVoice } from '../../../functions/useScenePlayback';

/**
 * A spoken line's waveform from its measured loudness, filling as it plays,
 * and its length (the time heard, while it plays); quiet stretches show as
 * dots.
 */
export const LineWaveform = memo(
  ({
    voice,
    turn,
    playing,
    position$,
  }: {
    voice: LineVoice;
    turn: number;
    playing: boolean;
    position$: BehaviorSubject<LinePosition | null>;
  }) => {
    const { rpx } = useResponsiveContext();
    // Only the line playing follows the ticks.
    const [heardMs, setHeardMs] = useState<number | null>(null);
    useEffect(() => {
      if (!playing) {
        setHeardMs(null);
        return;
      }
      const subscription = position$
        .pipe(
          map(x => (x?.turn === turn ? x.ms : null)),
          distinctUntilChanged(),
        )
        .subscribe(setHeardMs);
      return () => subscription.unsubscribe();
    }, [playing, position$, turn]);
    const bars = useMemo(
      () => buildAudioWaveform(voice.levels, waveformBars(voice.durationMs)),
      [voice],
    );
    const filled =
      heardMs == null
        ? 0
        : Math.round((heardMs / voice.durationMs) * bars.length);
    const rest = playing
      ? tint(aiColors.primary, 0.3)
      : tint(aiColors.primary, 0.6);
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            height: rpx(20),
            gap: rpx(2),
          }}
        >
          {bars.map((level, i) => (
            <View
              key={i}
              style={{
                width: rpx(2.5),
                height: Math.max(rpx(2.5), level * rpx(20)),
                borderRadius: rpx(1.25),
                backgroundColor: i < filled ? aiColors.primary : rest,
              }}
            />
          ))}
        </View>
        <Typography.Text
          size={rpx(12)}
          color={aiColors.mutedForeground}
          style={{ fontVariant: ['tabular-nums'] }}
        >
          {
            // The time heard counts up whole seconds; a length never reads
            // 0:00.
            formatVoiceDuration(
              heardMs != null
                ? Math.floor(heardMs / 1000)
                : Math.max(1, Math.ceil(voice.durationMs / 1000)),
            )
          }
        </Typography.Text>
      </View>
    );
  },
);
